import { UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { FinancingPlanType } from '@flexyoo/api/models';
import { WhiteLabelService } from '@flexyoo/common';
import { UserFinancingPlansService } from '@flexyoo/employee/data-access';
import { EmployeeSelectedBenefit } from '@flexyoo/employee/models';
import {
  EmployeeSelectedBenefitsActions,
  employeeSelectedBenefitsSelectors,
  employeeSettingsSelectors,
} from '@flexyoo/employee/store';
import {
  EmployeeCartSection,
  formatEmployeeCartDisplayName,
  getEmployeeCartBudgetSummary,
  getEmployeeCartPresentation,
  getEmployeeCartSections,
  isSalarySacrificeOnlyEmployee,
} from '@flexyoo/employee/util';
import { FlexyooNumberPipe } from '@flexyoo/shared/pipes';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, map, startWith, take } from 'rxjs';
import { PopUpService } from 'src/app/libs/shared/components/pop-up/pop-up.service';
import {
  EmployeeFinancialImpactReportComponent,
  NET_SIMULATION_DIALOG_INITIAL_WIDTH,
} from '../../employee-financial-impact-report/employee-financial-impact-report.component';
import { EmployeeBasketCartManagementRowComponent } from '../employee-basket-cart-management-row/employee-basket-cart-management-row.component';
import { EmployeeBasketCartSubmissionConfirmationDialogComponent } from '../employee-basket-cart-submission-confirmation-dialog/employee-basket-cart-submission-confirmation-dialog.component';

@Component({
  selector: 'flexyoo-employee-basket-cart-management',
  templateUrl: './employee-basket-cart-management.component.html',
  styleUrl: './employee-basket-cart-management.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UpperCasePipe,
    TranslateModule,
    MatIconModule,
    MatExpansionModule,
    MatButtonModule,
    MatTooltip,
    FlexyooNumberPipe,
    EmployeeBasketCartManagementRowComponent,
  ],
})
export class EmployeeBasketCartManagementComponent {
  private readonly store = inject(Store);
  private readonly popUpService = inject(PopUpService);
  private readonly translate = inject(TranslateService);
  private readonly userFinancingPlansService = inject(UserFinancingPlansService);
  private readonly dialog = inject(MatDialog);

  readonly selectedBenefitId = input<number | null>(null);
  readonly isCartSigningOpen = input(false);
  readonly removeBenefit = output<EmployeeSelectedBenefit>();
  readonly signBenefit = output<EmployeeSelectedBenefit>();
  readonly signCart = output<number>();
  readonly refresh = output<void>();

  protected readonly wls = inject(WhiteLabelService);
  protected readonly expanded = signal(true);

  protected readonly cart = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCurrentCartOrSimulatedDraft)
  );
  protected readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      map((change) => change.lang),
      startWith(this.translate.currentLang),
      distinctUntilChanged()
    ),
    { initialValue: this.translate.currentLang }
  );
  protected readonly benefits = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCurrentCartBenefits),
    { initialValue: [] }
  );
  protected readonly isPbEnabled = toSignal(
    this.userFinancingPlansService.userActivePlansInfo$.pipe(
      map((info) => info[FinancingPlanType.PrimesAndBonuses])
    )
  );
  protected readonly isSsEnabled = toSignal(
    this.userFinancingPlansService.userActivePlansInfo$.pipe(
      map((info) => info[FinancingPlanType.SalarySacrifice])
    )
  );
  protected readonly isSalarySacrificeOnly = toSignal(
    this.store
      .select(employeeSettingsSelectors.selectEmployeeSettings)
      .pipe(map((settings) => isSalarySacrificeOnlyEmployee(settings))),
    { initialValue: false }
  );

  protected readonly presentation = computed(() =>
    getEmployeeCartPresentation(this.cart()?.state, this.benefits(), this.cart()?.documents ?? [])
  );

  protected readonly isClearBasketDisabled = computed(
    () => !this.cart()?.id || this.presentation().isClearBasketDisabled
  );

  protected readonly isConfirmAndSignDisabled = computed(
    () =>
      !this.cart()?.id ||
      this.isInsufficientBudget() ||
      this.presentation().isConfirmAndSignDisabled ||
      this.isCartSigningOpen()
  );

  protected readonly sections = computed(() =>
    getEmployeeCartSections(this.cart()?.state, this.benefits(), {
      allowPerBenefitRemove: this.isSalarySacrificeOnly(),
    })
  );

  protected readonly showFooterActions = computed(() => {
    const presentation = this.presentation();
    return (
      presentation.showClearBasket ||
      presentation.showConfirmAndSign ||
      presentation.showConfirmAndResign
    );
  });

  protected readonly isInsufficientBudget = computed(() =>
    this.benefits().some((benefit) => benefit.hasInsufficientBudget)
  );

  protected readonly statusColor = computed(() => {
    const kind = this.presentation().kind;
    switch (kind) {
      case 'opened':
      case 'reopened':
        return 'rgba(0, 0, 0, 0.5)';
      case 'employee-approved':
        return this.wls.warnColor;
      case 'hr-approved':
        return this.wls.primaryColor;
      case 'rejected':
      case 'document-rejected':
      case 'signature-rejected':
        return 'var(--flexyoo-impersonated-banner-color)';
      default:
        return 'var(--flexyoo-impersonated-banner-color)';
    }
  });

  protected readonly statusTranslationKey = computed(() => {
    const kind = this.presentation().kind;
    const statusKind = kind === 'reopened' ? 'opened' : kind;

    return `employee.basket.cart-status.${statusKind}`;
  });

  protected readonly cartDisplayName = computed(() => {
    this.currentLang();
    const cart = this.cart();
    if (!cart) {
      return '';
    }

    if (cart.id !== null && cart.name) {
      return cart.name;
    }

    return formatEmployeeCartDisplayName(
      this.translate.instant('employee.basket.cart'),
      cart.period?.name,
      cart.period?.start
    );
  });

  protected readonly isOutsideChoicePeriod = computed(() => !this.cart()?.period);

  protected readonly summary = computed(() => getEmployeeCartBudgetSummary(this.benefits()));

  constructor() {
    toObservable(this.cart)
      .pipe(
        map((cart) => cart?.quillDocumentId ?? null),
        distinctUntilChanged(),
        filter((quillDocumentId): quillDocumentId is number => quillDocumentId != null),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        if (!this.presentation().isConfirmAndSignDisabled) {
          const cartId = this.cart()?.id;
          if (cartId !== null && cartId !== undefined) {
            this.signCart.emit(cartId);
          }
        }
      });
  }

  expandCart(): void {
    this.expanded.set(true);
  }

  protected sectionDotColor(section: EmployeeCartSection): string {
    switch (section.color) {
      case 'primary':
        return this.wls.primaryColor;
      case 'warn':
        return this.wls.warnColor;
      case 'accent':
        return this.wls.accentColor;
      case 'cancelled':
        return 'var(--flexyoo-impersonated-banner-color)';
    }
  }

  protected sectionCountBackgroundColor(section: EmployeeCartSection): string {
    switch (section.color) {
      case 'primary':
        return this.wls.primaryColor;
      case 'warn':
        return this.wls.warnColor;
      case 'accent':
        return this.wls.accentColor;
      case 'cancelled':
        return 'var(--flexyoo-impersonated-banner-color)';
    }
  }

  protected sectionCountTextColor(section: EmployeeCartSection): string {
    switch (section.color) {
      case 'primary':
        return this.wls.primaryContrastingColor;
      case 'warn':
        return this.wls.warnContrastingColor;
      case 'accent':
        return this.wls.primaryContrastingColor;
      case 'cancelled':
        return '#ffffff';
    }
  }

  protected onRemoveBenefit(benefit: EmployeeSelectedBenefit): void {
    const warningText = this.translate.instant('employee.basket.benefit-deletion-popup.warning');

    this.popUpService
      .openConfirmDialog({
        title: 'employee.basket.benefit-deletion-popup.title',
        text: this.translate.instant('employee.basket.benefit-deletion-popup.text', {
          name: this.translate.instant(benefit.name),
        }),
        htmlContent:
          '<p style="text-align: center; display: block; max-width: 300px; margin: 20px auto 0;">' +
          `<strong>${warningText}</strong></p>`,
        textYes: 'general.conf',
        textNo: 'general.cancel',
        swapButtons: true,
        width: '520px',
      })
      .pipe(filter(Boolean), take(1))
      .subscribe(() => {
        this.store.dispatch(EmployeeSelectedBenefitsActions.removeBenefit({ benefit }));
        this.removeBenefit.emit(benefit);
      });
  }

  protected onOpenBenefit(benefit: EmployeeSelectedBenefit): void {
    this.signBenefit.emit(benefit);
  }

  protected onSignCart(): void {
    if (this.isConfirmAndSignDisabled()) {
      return;
    }

    const cartId = this.cart()?.id;
    if (cartId === null || cartId === undefined) {
      return;
    }

    this.dialog
      .open<EmployeeBasketCartSubmissionConfirmationDialogComponent, void, boolean>(
        EmployeeBasketCartSubmissionConfirmationDialogComponent,
        {
          width: '520px',
          minWidth: '400px',
          maxWidth: '600px',
          disableClose: true,
          autoFocus: false,
          restoreFocus: false,
        }
      )
      .afterClosed()
      .pipe(filter(Boolean), take(1))
      .subscribe(() => {
        this.signCart.emit(cartId);
      });
  }

  protected onOpenFinancialImpactReport(): void {
    this.dialog.open(EmployeeFinancialImpactReportComponent, {
      width: NET_SIMULATION_DIALOG_INITIAL_WIDTH,
    });
  }

  protected onClearCart(): void {
    const cartId = this.cart()?.id;
    if (!cartId || this.isClearBasketDisabled()) {
      return;
    }

    this.popUpService
      .openConfirmDialog({
        title: 'employee.basket.cart-clear-popup.title',
        text: this.translate.instant('employee.basket.cart-clear-popup.text'),
        textYes: 'general.conf',
        textNo: 'general.cancel',
        swapButtons: true,
      })
      .pipe(filter(Boolean), take(1))
      .subscribe(() => {
        this.store.dispatch(EmployeeSelectedBenefitsActions.clearCart({ cartId }));
      });
  }
}
